import { formatDistanceToNow } from "date-fns";
import { enGB, sv } from "date-fns/locale";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion, AccordionContent, AccordionItem,
  AccordionTrigger, Avatar, Button, DatePicker, DivPaper, Grid, Heading, Icon, Link, MultiSelect, Option, Paper, Spacer, Text
} from "src/components";
import Circle from "src/components/spinner";
import { theme } from "src/config";
import { Item, Log } from "src/models";
import { ItemStores, LogStore, SpaceStore, UserStore } from "src/stores";
import { IItem, ItemType, LoadingStatus, SpaceType } from "src/types";
import { LogText } from "src/views/log";
import styled from "styled-components";
import { StatusAvatar } from "../room/editData";
import { NewsItemRow } from "./newsItemRow";


export interface INotificationsProps {
  spaceStore?: SpaceStore;
  userStore?:  UserStore;
  logStore?:   LogStore;
  itemStores?: ItemStores;
  data?:       Item<IItem>;
}

export const Notifications = inject("userStore", "meetingStore", "routingStore", "logStore", "itemStores", "spaceStore")(observer((props: INotificationsProps) => {
  const { spaceStore, logStore, userStore, itemStores } = props;
  const { t, i18n } = useTranslation();
  // const [project,           setProject]           = React.useState<Option>(undefined);
  // const [meetingRoom,       setMeetingRoom]       = React.useState<Option>(undefined);
  const [showFilterOptions, setShowFilterOptions] = React.useState(true);

  const containerRef = React.useRef<HTMLDivElement>();

  const last_logs: {[item_id: number]: Log} = logStore.getLastLogs;
  React.useEffect(() => {
    containerRef.current.addEventListener("scroll", () => {
      const winScroll = containerRef.current.scrollTop

      const height =
        containerRef.current.scrollHeight -
        containerRef.current.clientHeight;

      const scrolled = winScroll / height;
      
      if(scrolled > 0.9) {
        logStore.news.nextPage();
      }
    });
  }, [containerRef.current])

  React.useEffect( () => {
    if(![LoadingStatus.Next, LoadingStatus.Finished].includes(logStore.news.getStatus)) {
      <Circle
      size  = {20}
      color = {theme.color.slate12}
    />
    }
  }, [logStore.news.getStatus]);

  const toggleFilterOptions = React.useCallback(
    () => setShowFilterOptions(!showFilterOptions),
    [showFilterOptions]
  )

  // News Feed State
  const [roomIds,   setRoomIds]   = React.useState<number[]>([]);
  const [startDate, setStartDate] = React.useState<Date>();
  const [endDate,   setEndDate]   = React.useState<Date>();

  // Load news feed items when entering the dashboard
  React.useEffect(() => {
    logStore.news.setQueryParams({
      room_ids: roomIds,
      start: startDate,
      end: endDate
    });
    logStore.news.load();
    return () => {
      logStore.news.setQueryParams(null);
    };
  }, [roomIds, startDate, endDate]);

  // React.useEffect(() => {
  //   setRoomIds(meetingRoom ? [meetingRoom.value.id] : []);
  // }, [meetingRoom]);

  const locale = i18n.language == 'sv' ? sv : enGB;

  return (
    <StyledGrid alignContent="center" columns="2" flow="column">
      <NotificationColumn
        ref = {containerRef}
        $noShadow
      >
        <Header>
          <TitleContainer>
            <Heading style={{ textTransform: "capitalize" }} type="h3">
              {t("event_plural")}
            </Heading>
            {/* <StyledButton
              filterOptions = {!showFilterOptions}
              variant       = "outlined"
              size          = "default"
              iconPosition  = "left"
              iconId        = {!showFilterOptions ? "filter" : "close"}
              onClick       = {() => toggleFilterOptions()}
            >
              {t("filter")}
            </StyledButton> */}
          </TitleContainer>

          {showFilterOptions && (
            <>
              {/* <FilterContainer>
                <StyledMultiselect
                  options={spaceStore
                    .getUserSpaces(SpaceType.Project)
                    .map((p) => ({ label: p.title, value: p }))}
                  selected = {project ? [project] : []}
                  onChange = {setProject}
                  label    = {t("project")}
                  showLoadMore
                  singleSelect
                />
                <Spacer size="small" />
                <StyledMultiselect
                  options={spaceStore
                    .getUserSpaces(SpaceType.Room)
                    .filter((s) =>
                      project ? s && s.parent === project.value.id : true
                    )
                    .map((p) => ({ label: p.title, value: p }))}
                  selected = {meetingRoom ? [meetingRoom] : []}
                  onChange = {setMeetingRoom}
                  label    = {t("meeting_room")}
                  showLoadMore
                  singleSelect
                />
              </FilterContainer>*/}
              <Spacer size="small" /> 
              <DatePickerContainer>
                <DatePicker
                  showClearButton
                  label         = {t("start_date")}
                  value         = {startDate}
                  onChange      = {setStartDate}
                  calendarProps = {{
                    editable:           true,
                    selectable:         true,
                    weekNumbers:        true,
                    handleWindowResize: true
                  }}
                />
                <Spacer size = "small" />
                <DatePicker
                  showClearButton
                  label         = {t("end_date")}
                  value         = {endDate}
                  onChange      = {setEndDate}
                  calendarProps = {{
                    editable:           true,
                    selectable:         true,
                    weekNumbers:        true,
                    handleWindowResize: true
                  }}
                />
              </DatePickerContainer>
            </>
          )}
        </Header>

        <Accordion
          collapsible
          type          = "single"
          onValueChange = {(value: string) => logStore.setActiveLogItem(Number(value.split("_")[0]), Number(value.split("_")[1]))}
        >
          {logStore.news.getArray.filter(Boolean).map((log, i) => {
            const item: Item<IItem> = log.item;

            if(!item) return;
            return (
              <AccordionItem 
                key={log.item_id + "_" + log.room_id} 
                value={log.item_id + "_" + log.room_id} 
                style={{ backgroundColor: i % 2 ? theme.color.slate3 : '#fff', width: '100%' }}>
                <AccordionTrigger style={{ padding: "2rem 2rem" }}>
                  <TriggerContent>
                    <ItemBreadcrumb>
                      <Text
                        color = {theme.font.color.slate11}
                        size  = "small"
                        style = {{ display: "flex", flexDirection: "row" }}
                      >
                        {`${item.getProjectName} > ${item.getRoomName} >`}
                      </Text>
                      <Link
                        onClick={() =>
                          itemStores[item.data_type as ItemType].setActiveById(
                            item.id
                          )
                        }
                      >{` ${t(item.data_type)} #${item.number}`}</Link>
                    </ItemBreadcrumb>
                    <NotificationContainer>
                      <StatusAvatar data={item} variant="newsFeedItem"/>
                      <NotificationDetails>
                        <Text>{item.title}</Text>
                        <div
                          style={{ display: "flex", flexDirection: "column", gap: '0.5rem', width: "100%"}}
                        >
                          <Text
                            truncated
                            type  = "span"
                            color = {theme.color.slate11}
                            size  = "small"
                          >
                            <LogText log={last_logs[log.item_id]} />
                          </Text>
                          <AvatarWrapper>
                          <Avatar
                            size     = "default"
                            initials = {userStore.getItemByID(item.updated_by)?.getInitials}
                            image    = {
                              userStore.getItemByID(item.updated_by)
                                ?.getAvatar
                            }
                            />  
                            <Text
                              truncated
                              color = {theme.font.color.slate9}
                              size  = "xsmall"
                            >
                              {`${
                                userStore.getItemByID(item.updated_by)
                                  ?.getFullName
                              } ${formatDistanceToNow(item.updated_at, {
                                addSuffix: true,
                                locale
                              })} `}
                            </Text>
                          </AvatarWrapper>
                        </div>
                      </NotificationDetails>
                    </NotificationContainer>
                  </TriggerContent>
                </AccordionTrigger>
                <AccordionContent
                  style={{
                    width:         '100%',
                    display:       "flex",
                    flexDirection: "row",
                    paddingLeft:   "0rem"
                  }}
                >
                  <NewsItemRow newsFeedItem={true}/>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </NotificationColumn>
    </StyledGrid>
  );
}));

const StyledGrid = styled(Grid)`
  margin-bottom: 1rem;
`;

const StyledMultiselect = styled(MultiSelect)`
  flex: 1;
`;

const StyledButton = styled(Button)<{ filterOptions: boolean }>`
  background-color: ${(p) => (p.filterOptions ? "white" : "black")};
  color: ${(p) => (p.filterOptions ? "black" : "white")};
`;

const DatePickerContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  width:           100%;
  justify-content: space-between;
`;

const FilterContainer = styled.div`
  margin-top:     2rem;
  display:        flex;
  flex-direction: row;
`;
const ItemBreadcrumb = styled.div`
  margin-top:     0.25rem;
  display:        flex;
  flex-direction: row;
  gap:            0.5rem;
`;

const NotificationContainer = styled.div`
  display:        flex;
  flex-direction: row;
  align-items:    center;
  margin-top:     1.2rem;
`;

const TitleContainer = styled.div`
  display:         flex;
  flex-direction:  row;
  justify-content: space-between;
`;

const NotificationDetails = styled.div`
  display:        flex;
  flex-direction: column;
  text-align:     left;
  margin-left:    1rem;
  gap:            0.5rem;
`;

const AvatarWrapper = styled.div`
  display:        flex;
  flex-direction: row;
  align-items:    flex-start;
  white-space:    nowrap;
  overflow:       hidden;
  gap:            0.5rem;
`;

const TriggerContent = styled.div`
  display:        flex;
  flex-direction: column;
  align-items:    flex-start;
`;

const Header = styled.div`
  display:         flex;
  top:             0;
  background:      white;
  z-index:         99;
  position:        sticky;
  flex-direction:  column;
  justify-content: space-between;
  padding:         2.5rem 2.5rem;
  border-bottom:   1px solid ${theme.color.secondaryBorder};
`;

const NotificationColumn = styled(DivPaper)`
  width:   100%;
  height:  122rem;
  padding: 0;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
`;
